import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
    filterBalances,
    removeCommas,
    useJunoCommonFunctions,
    validateAmount,
    handleCryptoAmountValidation,
    validateInputAmount,
    toFixedRound,
} from '../../../../client/Juno/helpers';
import { apiService } from '../../../../client/common/apiCallService';
import ExchangeForm from './ExchangeForm';
import ExchangeModal from './ExchangeModal';
import TransactionStatus from '../../TransactionStatus';
import JunoButton from '../../common/JunoButton';

const ExchangeCryptoTransaction = props => {
    const { open: propOpen, onClose: onCloseModal, row: selectedCurrency } = props;
    // Modal state management
    const [open, setOpen] = useState(false);
    // const onClose = () => {
    //     setOpen(false);
    //     onCloseModal?.();
    // };

    useEffect(() => {
        propOpen ? setOpen(true) : setOpen(false);
    }, [propOpen]);

    // Redux state and hooks
    const apiUrl = useSelector(state => state.config.api_url);
    const {
        balancesCrypto = [],
        clientData,
        unfreezeCryptoCurrencies,
    } = useSelector(state => state.juno);

    const { handleCryptoExchangeRate, getCryptoBalances, getCryptoFee, getTransactions } =
        useJunoCommonFunctions();

    // Filtered balances
    const updatedUnfreeze = useMemo(
        () => filterBalances(unfreezeCryptoCurrencies),
        [unfreezeCryptoCurrencies],
    );

    // Component state
    const [formData, setFormData] = useState({
        fromCrypto: null,
        toCrypto: null,
        fromAmount: '',
        toAmount: '',
    });

    const [fieldError, setFieldError] = useState({
        fromCrypto: '',
        toCrypto: '',
        fromAmount: '',
        toAmount: '',
    });

    const [exchangeRate, setExchangeRate] = useState(null);
    const [fee, setFee] = useState(null); // Changed from 0 to null
    const [lastEnteredInput, setLastEnteredInput] = useState('fromAmount');
    const [isConfirmModal, setIsConfirmModal] = useState(false);
    const [quotationData, setQuotationData] = useState({});
    const [transactionFee, setTransactionFee] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingRate, setLoadingRate] = useState(false);
    const [netExchangeAmount, setNetExchangeAmount] = useState(0);
    const [continueBtn, setContinueBtn] = useState(false);

    const quotationRefreshInterval = useRef();
    const modalTimerRef = useRef(null);

    // Status modals
    const [successModal, setSuccessModal] = useState(false);
    const [failedModal, setFailedModal] = useState({ open: false, msg: '' });
    const [pendingModal, setPendingModalModal] = useState({ open: false, msg: '' });

    // Initialize form data with available currencies
    useEffect(() => {
        if (updatedUnfreeze?.length >= 2 && !formData.fromCrypto && !formData.toCrypto) {
            let fromCrypto = selectedCurrency || updatedUnfreeze[0];
            let toCrypto =
                updatedUnfreeze.find(
                    crypto => crypto.currencyShortName !== fromCrypto.currencyShortName,
                ) || updatedUnfreeze[1];

            setFormData({
                fromCrypto,
                toCrypto,
                fromAmount: '',
                toAmount: '',
            });
        }
    }, [updatedUnfreeze, formData.fromCrypto, formData.toCrypto, selectedCurrency]);

    // Handle closing the modal and resetting state
    const handleClose = (resetForm = true) => {
        // Clean up interval
        cleanUpInterval();

        setOpen(false);
        setIsLoading(false);
        setIsConfirmModal(false);

        if (resetForm) resetFormData();

        setQuotationData({});
        clearTimeout(modalTimerRef.current);
        setContinueBtn(false);
    };

    // Reset form data
    const resetFormData = () => {
        setFormData({
            fromCrypto: updatedUnfreeze[0] || null,
            toCrypto: updatedUnfreeze[1] || null,
            fromAmount: '',
            toAmount: '',
        });

        setFieldError({
            fromCrypto: '',
            toCrypto: '',
            fromAmount: '',
            toAmount: '',
        });
        setFee(null)
        fetchRateAndFee()
    };

    // Fetch exchange rate and fee
    const fetchRateAndFee = async () => {
        const { fromCrypto, toCrypto } = formData;
        if (!fromCrypto || !toCrypto) return;

        try {
            setLoadingRate(true);

            if (!isConfirmModal) {
                const payload = {
                    cryptoList: [toCrypto.currencyShortName],
                    cryptoAmount: 1,
                    cryptoCurrency: fromCrypto.currencyShortName,
                    exchangeConversionType: 'crypto-to-crypto',
                };

                const response = await handleCryptoExchangeRate(payload);
                if (response?.cryptoAmounts?.[toCrypto.currencyShortName]) {
                    const rate = Number(response?.cryptoAmounts?.[toCrypto.currencyShortName]);
                    setExchangeRate(isNaN(rate) ? 0 : +rate.toFixed(8));
                    setFieldError(prev => ({
                        ...prev,
                        fromAmount: '',
                    }));
                } else {
                    setExchangeRate(null);
                    setFieldError(prev => ({
                        ...prev,
                        fromAmount: 'Failed to fetch exchange rates. Please try again.',
                    }));
                }
            }

            const feePayload = {
                crypto: fromCrypto.currencyShortName,
                toCurrency: toCrypto.currencyShortName,
                profile: clientData?.feeProfile,
                transactionType: 'Exchange',
            };

            const cryptoFee = await getCryptoFee(feePayload);
            // Keep fee as null if API call fails or returns error
            const feeValue = !cryptoFee?.error ? Number(cryptoFee?.data?.fee) : NaN;
            setFee(!isNaN(feeValue) ? feeValue / 100 : null);
        } catch (error) {
            console.error('Error fetching rate or fee:', error);
            setExchangeRate(null);
            setFee(null); // Set fee to null on error
            setFieldError(prev => ({
                ...prev,
                fromAmount: 'Failed to fetch exchange rates. Please try again.',
            }));
        } finally {
            setLoadingRate(false);
        }
    };

    // Fetch rate and fee when currencies change
    useEffect(() => {
        fetchRateAndFee();
    }, [formData.fromCrypto, formData.toCrypto, clientData, open]);

    const handleAmountChange = (field, value) => {
        if (!exchangeRate) return;

        // Prevent calculations if fee is null
        if (fee === null) {
            setFieldError(prev => ({
                ...prev,
                toCrypto: 'Unable to fetch transaction fee. Please try again.',
                fromCrypto: 'Unable to fetch transaction fee. Please try again.'
            }));
            return;
        }

        const sanitizedValue = removeCommas(validateInputAmount(value));
        setLastEnteredInput(field);

        const numValue = parseFloat(sanitizedValue);

        let newFromAmount;
        let newToAmount;
        let transactionFeeAmount;
        if (field === 'fromAmount') {
            // Scenario 1: User enters "From" amount (e.g., 10 USDC)
            // Fee is deducted from the entered amount before exchange
            newFromAmount = sanitizedValue;
            if (isNaN(numValue)) {
                newToAmount = '';
            } else {
                // Calculate transaction fee (e.g., 1% of 10 USDC = 0.10 USDC)
                transactionFeeAmount = +(numValue * fee).toFixed(8);
                // Net amount sent to exchange (e.g., 10 - 0.10 = 9.90 USDC)
                const netAmountForExchange = numValue - transactionFeeAmount;
                // Calculate "To" amount based on net amount (e.g., 9.90 USDC * rate = ETH amount)
                newToAmount = netAmountForExchange * exchangeRate;
                newToAmount = toFixedRound(newToAmount, 8);
            }
        } else if (field === 'toAmount') {
            // Scenario 2: User enters "To" amount (e.g., 1 ETH)
            // Calculate total "From" amount needed including fee
            newToAmount = sanitizedValue;
            if (isNaN(numValue)) {
                newFromAmount = '';
            } else {
                // Calculate base amount needed before fee (e.g., 1 ETH / rate = 2,462.43 USDC)
                const baseAmountNeeded = numValue / exchangeRate;
                // Calculate total amount user needs to pay including fee
                // (e.g., 2,462.43 / (1 - 0.01) = 2,487.05 USDC)
                newFromAmount = baseAmountNeeded / (1 - fee);
                newFromAmount = toFixedRound(newFromAmount, 8);
                transactionFeeAmount = +(newFromAmount * fee).toFixed(8);
            }
        }

        setTransactionFee(transactionFeeAmount);
        const fromCryptoValidationError = handleCryptoAmountValidation(
            removeCommas(newFromAmount),
            formData.fromCrypto?.currencyShortName,
            balancesCrypto,
        );

        const toCryptoValidationError = handleCryptoAmountValidation(
            removeCommas(newToAmount),
            formData.toCrypto?.currencyShortName,
            balancesCrypto,
        );

        const fromValidationAmountError = validateAmount(
            removeCommas(newFromAmount),
            0,
            formData.fromCrypto?.currencyShortName,
            balancesCrypto,
        );

        let toValidationAmountError;
        if (!parseFloat(removeCommas(newToAmount) || 0) > 0) {
            toValidationAmountError = `* ${formData.toCrypto?.currencyShortName} Amount must be greater than 0`;
        }

        setFormData({
            ...formData,
            fromAmount: newFromAmount,
            toAmount: newToAmount,
        });

        setFieldError(prev => ({
            ...prev,
            fromAmount: fromValidationAmountError || fromCryptoValidationError || '',
            toAmount: toValidationAmountError || toCryptoValidationError || '',
            quotation: '',
        }));
    };

    // Handle currency changes
    const handleCurrencyChange = (field, currency) => {
        const oppositeField = field === 'fromCrypto' ? 'toCrypto' : 'fromCrypto';

        if (formData[oppositeField]?.currencyShortName === currency?.currencyShortName) {
            setFieldError(prev => ({
                ...prev,
                toCrypto: 'From and to crypto cannot be the same',
                fromCrypto: field === 'fromCrypto' ? 'From and to crypto cannot be the same' : '',
            }));
        } else {
            setFieldError(prev => ({
                ...prev,
                toCrypto: '',
                fromCrypto: '',
            }));
        }

        setFormData({
            ...formData,
            [field]: currency,
            fromAmount: '',
            toAmount: '',
        });
    };

    const handleQuotationAmount = () => {
        if (
            !formData.fromCrypto ||
            !formData.toCrypto ||
            (!formData.fromAmount && !formData.toAmount) ||
            fee === null // Add null check for fee
        ) {
            return;
        }

        const blockchain = balancesCrypto.find(
            crypto => crypto.currencyShortName === formData.toCrypto?.currencyShortName,
        )?.blockchain;

        const isFromCryptoInput = lastEnteredInput === 'fromAmount';
        const isToCryptoInput = lastEnteredInput === 'toAmount';

        const cryptoAmountForTrade = parseFloat(formData.fromAmount) - transactionFee;

        const payload = {
            userId: clientData?.accountNumber,
            toCrypto: formData.toCrypto?.currencyShortName,
            blockchain: blockchain,
            ...(isFromCryptoInput && {
                fromCryptoAmount: Number(removeCommas(cryptoAmountForTrade)).toFixed(6),
            }),
            ...(isToCryptoInput && {
                toCryptoAmount: Number(removeCommas(formData.toAmount)).toFixed(6),
            }),
            side: 'Buy',
            fromCrypto: formData.fromCrypto?.currencyShortName,
        };

        apiService(
            (window.location.hostname === 'localhost' ? apiUrl : '') +
            '/restapi/request-for-quotation',
            payload,
            async res => {
                if (!res.error && res?.data?.quotationId1) {
                    setQuotationData(res.data);

                    const feeRate = parseFloat(fee);
                    const updatedFormData = { ...formData };

                    if (lastEnteredInput === 'toAmount') {
                        const deliverQty = +res.data.deliverQuantity;
                        const transactionFee = +(deliverQty * feeRate).toFixed(8);
                        const totalFromAmount = +(deliverQty + transactionFee).toFixed(8);
                        updatedFormData.fromAmount = toFixedRound(totalFromAmount, 8);
                        setTransactionFee(transactionFee);
                        setNetExchangeAmount(deliverQty);
                    } else if (lastEnteredInput === 'fromAmount') {
                        updatedFormData.toAmount = toFixedRound(res.data.receiveQuantity, 8);
                        setNetExchangeAmount(cryptoAmountForTrade);
                    }

                    setFormData(updatedFormData);
                } else {
                    setQuotationData({ error: true });
                }
            },
            err => {
                console.error('Error fetching quotation:', err);
            },
        );
    };

    const cleanUpInterval = () => {
        if (quotationRefreshInterval.current) {
            clearInterval(quotationRefreshInterval.current);
            quotationRefreshInterval.current = null;
        }
    };

    // Auto-refresh the quotation on interval
    useEffect(() => {
        // Clean up any existing interval first
        cleanUpInterval();

        if (open && isConfirmModal && !continueBtn) {
            // Set up new interval
            const intervalId = setInterval(() => {
                handleQuotationAmount();
            }, 5000);

            quotationRefreshInterval.current = intervalId;
        }

        // Clean up interval on component unmount
        return () => {
            if (quotationRefreshInterval.current) {
                clearInterval(quotationRefreshInterval.current);
            }
        };
    }, [open, isConfirmModal, continueBtn]);

    // Handle confirmation modal
    const handleConfirmationModal = () => {
        const hasErrors = Object.values(fieldError).some(error => error !== '');
        const hasValidAmounts = formData.fromAmount && formData.toAmount;

        if (hasValidAmounts && !hasErrors) {
            setIsConfirmModal(true);
            handleQuotationAmount();
        }

        modalTimerRef.current = setTimeout(() => {
            setFieldError(prev => ({
                ...prev,
                quotation: 'Quote expired, please re-enter the amounts.',
            }));
            setIsConfirmModal(false);
            fetchRateAndFee();

            setTimeout(() => {
                setFieldError(prev => ({
                    ...prev,
                    quotation: '',
                }));
            }, 10000);
        }, 30000);
    };

    // Create transaction
    const handleCreateTransaction = () => {
        setIsLoading(true);
        clearTimeout(modalTimerRef.current);
        setContinueBtn(true);

        const blockchain = balancesCrypto.find(
            crypto => crypto.currencyShortName === formData.toCrypto?.currencyShortName,
        )?.blockchain;

        const payload = {
            currencyType: 'crypto',
            type: 'Exchange',
            transactionEmail: localStorage.getItem('user_name'),
            transactionFee: transactionFee,
            transactionDetails: {
                quotationId1: quotationData?.quotationId1,
                quotationId2: quotationData?.quotationId2,
                fromCryptoAmount: removeCommas(formData.fromAmount),
                toCryptoAmount: removeCommas(formData.toAmount),
                fromCryptoId: formData.fromCrypto?.currencyShortName,
                toCryptoId: formData.toCrypto?.currencyShortName,
                blockchain,
                exchangeRate: +parseFloat(exchangeRate).toFixed(8),
                quotationPrice: +parseFloat(quotationData?.price),
                fee: fee * 100, // Convert fee to percentage
                quotationSide: quotationData?.side,
                // Add net amount for exchange to transaction details
                netExchangeAmount,
                calculationMethod: lastEnteredInput,
            },
        };

        apiService(
            (window.location.hostname === 'localhost' ? apiUrl : '') +
            '/restapi/create-client-transaction-crypto',
            payload,
            async data => {
                // Clean up interval before closing
                cleanUpInterval();

                getCryptoBalances();
                handleClose(false);
                getTransactions();

                if (data?.status === 'success') {
                    setSuccessModal(true);
                } else if (data?.status === 'pending') {
                    setPendingModalModal({ open: true, msg: data?.msg });
                } else {
                    setFailedModal({ open: true, msg: data?.msg });
                }
            },
            err => {
                console.error('Error creating transaction:', err);
                setFailedModal({ open: true, msg: err });
                setOpen(false);
            },
        );
    };

    // Handle modals
    const handleSuccessModal = () => {
        setSuccessModal(false);
        resetFormData();
        onCloseModal?.();
    };

    const handleFailedModal = () => {
        setFailedModal({ open: false, msg: '' });
        resetFormData();
        setIsConfirmModal(false);
        handleClose();
        onCloseModal?.();
    };

    const handleProcessModal = () => {
        setPendingModalModal({ open: false, msg: '' });
        resetFormData();
        setIsConfirmModal(false);
        onCloseModal?.();
        getCryptoBalances();
        getTransactions();
    };

    // Create props object for child components
    const exchangeProps = useMemo(
        () => ({
            formData,
            fieldError,
            exchangeRate,
            loadingRate,
            fee,
            quotationData,
            isLoading,
            isConfirmModal,
            updatedUnfreeze,
            transactionFee, // Add transaction fee to props
            handleAmountChange,
            handleCurrencyChange,
            setFormData,
            setFieldError,
            lastEnteredInput,
        }),
        [
            formData,
            fieldError,
            exchangeRate,
            loadingRate,
            fee,
            quotationData,
            isLoading,
            isConfirmModal,
            updatedUnfreeze,
            transactionFee,
        ],
    );

    return (
        <>
            {/* Standalone form when not in modal mode */}
            {!propOpen && (
                <div className="flex justify-between gap-2.5 flex-col h-full">
                    <div className="bg-modal-bg p-3 w-full h-[444px] rounded-lg border border-[#E4E4E7] dark:!border-0 bg-quickforms-bg">
                        <ExchangeForm {...exchangeProps} />
                    </div>
                    <JunoButton
                        type="widget"
                        onClick={() => {
                            setOpen(true);
                            handleConfirmationModal();
                        }}
                        disabled={
                            Object.values(fieldError).some(error => error !== '') ||
                            !exchangeRate ||
                            !formData.fromAmount ||
                            !formData.toAmount ||
                            fee === null
                        }
                        dataE2e="continue-button">
                        Continue
                    </JunoButton>
                </div>
            )}

            {/* Modal version */}
            <ExchangeModal
                {...exchangeProps}
                isOpen={open}
                onClose={() => {
                    handleClose();
                    onCloseModal?.();
                }}
                onBack={() => {
                    setIsConfirmModal(false);
                    setQuotationData({});
                    clearTimeout(modalTimerRef.current);
                }}
                handleConfirmationModal={handleConfirmationModal}
                onClickCall={handleCreateTransaction}
            />

            {/* Status modals */}

            {successModal && (
                <TransactionStatus
                    status="success"
                    currency={formData.fromCrypto?.currencyShortName}
                    amount={formData.fromAmount}
                    toAmount={formData.toAmount}
                    toCurrency={formData.toCrypto?.currencyShortName}
                    transactionType={'EXCHANGE'}
                    description="Has been deposited into your Juno X wallet."
                    onClick={handleSuccessModal}
                    buttonText="CLOSE"
                />
            )}
            {pendingModal.open && (
                <TransactionStatus
                    status="pending"
                    currency={formData.fromCrypto?.currencyShortName}
                    amount={formData.fromAmount}
                    toAmount={formData.toAmount}
                    toCurrency={formData.toCrypto?.currencyShortName}
                    description="Exchange pending, check your transaction history to track it's progress"
                    onClick={handleProcessModal}
                    buttonText="Track Transaction"
                    transactionType={'EXCHANGE'}
                />
            )}

            {/* Failed/Error modal */}
            {failedModal.open && (
                <TransactionStatus
                    status="error"
                    currency={formData.fromCrypto?.currencyShortName}
                    amount={formData.fromAmount}
                    toAmount={formData.toAmount}
                    toCurrency={formData.toCrypto?.currencyShortName}
                    description={failedModal.msg || ''}
                    onClick={handleFailedModal}
                    buttonText="CLOSE"
                    transactionType={'EXCHANGE'}
                />
            )}
        </>
    );
};

export default ExchangeCryptoTransaction;